import type { ServiceOptions} from '@/types/core/api'

/**
 * Fluent API Builder for configuring service options
 * 
 * @example
 * ```typescript
 * // Basic usage with success message
 * await staffService.getAdminManagers(
 *   staffService.config<StaffManager[]>()
 *     .showSuccess('載入管理員清單成功')
 *     .build()
 * )
 * 
 * // Complex usage with error handling and retry
 * await staffService.addAdminStaff(
 *   userData,
 *   staffService.config<void>()
 *     .showSuccess('新增成功')
 *     .retry(2)
 *     .onSuccess(() => loadManagers())
 *     .on422((errors) => handleValidationErrors(errors))
 *     .build()
 * )
 * ```
 */
export class ApiBuilder<T> {
  private readonly options: ServiceOptions<T> = {
    // 預設值
    message: {
      showSuccessMessage: false,  // 預設不顯示成功訊息
      showErrorMessage: true      // 預設顯示錯誤訊息
    },
    error: {
      enableRetry: false,
      maxRetries: 0,
      enableErrorTracking: true
    }
  }

  // 成功訊息相關
  showSuccess(message?: string | boolean): this {
    this.options.message ??= {}
    this.options.message.showSuccessMessage = message === false ? false : (message || true)
    return this
  }

  hideSuccess(): this {
    this.options.message ??= {}
    this.options.message.showSuccessMessage = false
    return this
  }

  // 錯誤訊息相關
  showError(message?: string | boolean): this {
    this.options.message ??= {}
    this.options.message.showErrorMessage = message === false ? false : (message || true)
    return this
  }

  hideError(): this {
    this.options.message ??= {}
    this.options.message.showErrorMessage = false
    return this
  }

  // 完全靜默
  silent(): this {
    this.options.message = {
      showSuccessMessage: false,
      showErrorMessage: false
    }
    return this
  }

  // 重試機制
  retry(times: number = 3, delay: number = 1000): this {
    this.options.error ??= {}
    this.options.error.enableRetry = true
    this.options.error.maxRetries = times
    this.options.error.retryDelay = delay
    return this
  }

  // 載入狀態
  loading(message?: string | boolean): this {
    this.options.loading = message === false ? false : (message || true)
    return this
  }

  // API Loading - 資料載入等非阻塞操作
  apiLoading(message?: string, completedMessage?: string): this {
    this.options.loading = {
      type: 'api',
      message: message ?? '載入中...',
      completedMessage
    }
    return this
  }

  // Form Loading - 表單操作等阻塞操作
  formLoading(message?: string, completedMessage?: string): this {
    this.options.loading = {
      type: 'form',
      message: message ?? '處理中...',
      completedMessage
    }
    return this
  }

  // 成功回調
  onSuccess(callback: (data: T) => void | Promise<void>): this {
    this.options.onSuccess = callback
    return this
  }

  // 錯誤回調
  onError(callback: (error: any) => void | Promise<void>): this {
    this.options.onError = callback
    return this
  }

  // 最終回調
  onFinally(callback: () => void | Promise<void>): this {
    this.options.onFinally = callback
    return this
  }

  // 特定錯誤處理
  on401(callback: () => void | Promise<void>): this {
    this.options.error ??= {}
    this.options.error.handle401 = callback
    return this
  }

  on403(callback: () => void | Promise<void>): this {
    this.options.error ??= {}
    this.options.error.handle403 = callback
    return this
  }

  on422(callback: (validationErrors: Record<string, string[]>) => void): this {
    this.options.error ??= {}
    this.options.error.handle422 = callback
    return this
  }

  on404(callback: () => void | Promise<void>): this {
    this.options.error ??= {}
    this.options.error.handle404 = callback
    return this
  }

  on500(callback: () => void | Promise<void>): this {
    this.options.error ??= {}
    this.options.error.handle500 = callback
    return this
  }

  // 錯誤追蹤
  trackError(context?: Record<string, any>): this {
    this.options.error ??= {}
    this.options.error.enableErrorTracking = true
    if (context) {
      this.options.error.errorContext = context
    }
    return this
  }

  noTracking(): this {
    this.options.error ??= {}
    this.options.error.enableErrorTracking = false
    return this
  }

  // 建立最終選項
  build(): ServiceOptions<T> {
    return this.options
  }
}